import React, { useState, useEffect, useCallback } from 'react';
import { ShoppingCart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

/**
 * Aggregates every open cart the buyer has (one per seller) by scanning
 * `cart:${sellerId}` + `cart-meta:${sellerId}` localStorage entries written
 * by SellerProfile. Provides a header button with a badge showing the total
 * items across every seller, and a modal listing each seller with a link to
 * open the profile and complete checkout there.
 */
const readCarts = () => {
  const out = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (!key || !key.startsWith('cart:') || key.startsWith('cart-meta:')) continue;
    const sellerId = key.slice('cart:'.length);
    if (!sellerId) continue;
    let items = {};
    try { items = JSON.parse(localStorage.getItem(key) || '{}'); } catch { items = {}; }
    const totalQty = Object.values(items).reduce((sum, q) => sum + (q || 0), 0);
    if (totalQty <= 0) continue;
    let meta = {};
    try { meta = JSON.parse(localStorage.getItem(`cart-meta:${sellerId}`) || '{}'); } catch { meta = {}; }
    out.push({
      seller_id: sellerId,
      total_qty: totalQty,
      distinct_items: Object.keys(items).length,
      business_name: meta.business_name || 'Proveedor',
      logo_url: meta.logo_url || null,
      updated_at: meta.updated_at || null,
    });
  }
  out.sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''));
  return out;
};

const MyCartsButton = () => {
  const [open, setOpen] = useState(false);
  const [carts, setCarts] = useState([]);

  const refresh = useCallback(() => setCarts(readCarts()), []);

  useEffect(() => {
    refresh();
    const onStorage = () => refresh();
    window.addEventListener('storage', onStorage);
    const t = setInterval(refresh, 4000); // refresh when returning from a seller tab
    return () => { window.removeEventListener('storage', onStorage); clearInterval(t); };
  }, [refresh]);

  const totalItems = carts.reduce((s, c) => s + c.total_qty, 0);

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        data-testid="my-carts-button"
        onClick={() => { refresh(); setOpen(true); }}
        className="relative text-slate-600 hover:text-slate-900"
      >
        <ShoppingCart size={20} />
        {totalItems > 0 && (
          <span
            data-testid="my-carts-count"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-emerald-600 text-white text-[10px] font-bold flex items-center justify-center"
          >
            {totalItems > 99 ? '99+' : totalItems}
          </span>
        )}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="my-carts-dialog" className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Mis carritos</DialogTitle>
            <DialogDescription>
              Un carrito por proveedor. Abre el perfil para terminar el pedido.
            </DialogDescription>
          </DialogHeader>
          {carts.length === 0 ? (
            <p className="text-sm text-slate-500 py-8 text-center" data-testid="my-carts-empty">
              Todavía no has añadido nada. Explora los proveedores y añade productos a tu carrito.
            </p>
          ) : (
            <ul className="space-y-2 mt-2">
              {carts.map((c) => (
                <li
                  key={c.seller_id}
                  data-testid={`my-cart-item-${c.seller_id}`}
                  className="border border-slate-200 rounded-lg p-3 flex items-center gap-3"
                >
                  <div className="w-10 h-10 rounded-md bg-slate-100 overflow-hidden flex items-center justify-center flex-shrink-0">
                    {c.logo_url ? (
                      <img src={c.logo_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <ShoppingCart size={18} className="text-slate-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">{c.business_name}</p>
                    <p className="text-xs text-slate-500">
                      {c.total_qty} artículo{c.total_qty === 1 ? '' : 's'} · {c.distinct_items} producto{c.distinct_items === 1 ? '' : 's'}
                    </p>
                  </div>
                  <a
                    href={`/proveedor/${c.seller_id}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`my-cart-open-${c.seller_id}`}
                    className="text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-md"
                  >
                    Abrir
                  </a>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MyCartsButton;
